import { Col, DatePicker, Form, Row, TimePicker } from 'antd';
import Icon from 'assets/Icons';
import Box from 'components/Box';
import Button from 'components/Button';
import Text from 'components/Text';
import dayjs, { Dayjs } from 'dayjs';
import settingServiceActions from 'features/settingService/services/actions';
import settingServiceSelectors from 'features/settingService/services/selectors';
import React from 'react';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { momentTimezone } from 'utils/time';

type IDaysOffListProps = {
  v2?: boolean;
};
const DaysOffList: React.FC<IDaysOffListProps> = ({ v2 }) => {
  const dispatch = useAppDispatch();
  const disableDateList = settingServiceSelectors.getDayOffList();
  const [form] = Form.useForm();
  const totalDays = disableDateList.reduce((sum, data) => {
    const startTime = momentTimezone(data.startTime);
    const endTime = momentTimezone(data.endTime);
    const daysCount = endTime.diff(startTime, 'day') + 1;
    return sum + daysCount;
  }, 0);

  const handleCreateDayOff = (values: any) => {
    const startTime = `${values.startDate.format('MM-DD-YYYY')} ${values.startTime.format('HH:mm')}:00`;
    const endTime = `${values.endDate.format('MM-DD-YYYY')} ${values.endTime.format('HH:mm')}:00`;
    const param = {
      startTime,
      endTime,
    };
    dispatch(settingServiceActions.postDayOffList.fetch(param));
    form.resetFields();
  };
  const handleDelete = (id: string) => {
    dispatch(settingServiceActions.deleteDayOffList.fetch(id));
  };

  return (
    <DaysOffListStyled className={v2 ? 'v2' : ''}>
      <Box>
        <Box display="flex" alignItems="center" height="100%">
          <Text variant="H7" color="text_3" className={'section-label'}>
            Select To Create Day Off
          </Text>
        </Box>
        <RangerContainerStyled onFinish={handleCreateDayOff} form={form} className="RangerContainerStyled">
          <Box width="100%">
            <PickerContainer display='flex' alignItems='center'>
              <Form.Item label="Start Date:" >
                <Box display="flex" alignItems="center">
                  <ItemFormStyled name="startDate" noStyle={v2} rules={[{ required: true, message: 'Please select start date!' }]}>
                    <DatePickerStyled
                      bordered={false}
                      allowClear={false}
                      suffixIcon={null}
                      disabledDate={(current: Dayjs) => current && current < dayjs().startOf('day')}
                      dropdownClassName='dropdown-date-picker-v2'
                      style={{ width: '20vw' }}
                      format={'MM-DD-YYYY'}
                    />
                  </ItemFormStyled>
                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.startDate !== currentValues.startDate}>
                    {({ getFieldValue }) => {
                      const startDate = getFieldValue('startDate');
                      return (
                        <ItemFormStyled name="startTime" noStyle={v2} rules={[{ required: true, message: 'Please select start time!' }]}>
                          <TimePickerStyled
                            use12Hours
                            bordered={false}
                            allowClear={false}
                            suffixIcon={null}
                            className="TimePickerStyled"
                            format="hh:mm A"
                            style={{ width: '10vw' }}
                            dropdownClassName='dropdown-date-picker-v2'
                            disabledTime={() => {
                              const now = dayjs();
                              const isToday = startDate && startDate.isSame(now, 'day');
                              if (!isToday) return {};

                              const currentHour = now.hour();
                              const currentMinute = now.minute();

                              return {
                                disabledHours: () => Array.from({ length: currentHour }, (_, i) => i),
                                disabledMinutes: (selectedHour: number) => {
                                  if (selectedHour === currentHour) {
                                    return Array.from({ length: currentMinute + 1 }, (_, i) => i);
                                  }
                                  return [];
                                }
                              };
                            }}
                          />
                        </ItemFormStyled>
                      );
                    }}
                  </Form.Item>
                </Box>
              </Form.Item>
              <Form.Item label="End Date:" >
                <Box display="flex" alignItems="center">
                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.startDate !== currentValues.startDate}>
                    {({ getFieldValue }) => {
                      const startDate = getFieldValue('startDate');
                      return (
                        <ItemFormStyled name="endDate" noStyle={v2} rules={[{ required: true, message: 'Please select end date!' }]}>
                          <DatePickerStyled
                            bordered={false}
                            allowClear={false}
                            suffixIcon={null}
                            disabledDate={(current: Dayjs) => {
                              if (!current) return false;
                              if (current < dayjs().startOf('day')) return true;
                              if (startDate && current < startDate.startOf('day')) return true;
                              return false;
                            }}
                            dropdownClassName='dropdown-date-picker-v2'
                            style={{ width: '20vw' }}
                            format={'MM-DD-YYYY'}
                          />
                        </ItemFormStyled>
                      );
                    }}
                  </Form.Item>
                  <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.startDate !== currentValues.startDate || prevValues.startTime !== currentValues.startTime || prevValues.endDate !== currentValues.endDate}>
                    {({ getFieldValue }) => {
                      const startDate = getFieldValue('startDate');
                      const startTime = getFieldValue('startTime');
                      const endDate = getFieldValue('endDate');
                      return (
                        <ItemFormStyled name="endTime" noStyle={v2} rules={[{ required: true, message: 'Please select end time!' }]}>
                          <TimePickerStyled
                            use12Hours
                            bordered={false}
                            allowClear={false}
                            suffixIcon={null}
                            className="TimePickerStyled"
                            format="hh:mm A"
                            style={{ width: '10vw' }}
                            dropdownClassName='dropdown-date-picker-v2'
                            disabledTime={() => {
                              const isSameDay = startDate && endDate && startDate.isSame(endDate, 'day');
                              if (!isSameDay || !startTime) return {};

                              const startHour = startTime.hour();
                              const startMinute = startTime.minute();

                              return {
                                disabledHours: () => Array.from({ length: startHour + 1 }, (_, i) => i),
                                disabledMinutes: (selectedHour: number) => {
                                  if (selectedHour === startHour) {
                                    return Array.from({ length: startMinute + 1 }, (_, i) => i);
                                  }
                                  return [];
                                }
                              };
                            }}
                          />
                        </ItemFormStyled>
                      );
                    }}
                  </Form.Item>
                </Box>
              </Form.Item>
              <Form.Item label={<div style={{ opacity: 0 }}>__</div>}>
                <Form.Item shouldUpdate noStyle>{({ getFieldValue }) => {
                  const startDate = getFieldValue('startDate');
                  const startTime = getFieldValue('startTime');
                  const endDate = getFieldValue('endDate');
                  const endTime = getFieldValue('endTime');
                  const isDateRangeFilled = startDate && startTime && endDate && endTime;
                  return (
                    <Button
                      cardCashier={v2}
                      size={v2 ? undefined : 'large'}
                      height={!v2 ? undefined : 3.5}
                      disabled={!isDateRangeFilled}
                      ntype="PRIMARY"
                      htmlType='button'
                      onClick={() => form.submit()}
                    >
                      Create
                    </Button>
                  );
                }}</Form.Item>
              </Form.Item>
            </PickerContainer>
          </Box>
        </RangerContainerStyled>
      </Box>

      <TimeListContainer className='high-order' mt="4" display='flex' gap='6' alignSelf='stretch'>
        <Box width={'10vw'}>
          <Text variant="H7" color="text_3" className={'section-label'}>
            Number of Date Offs:
          </Text>
          <Text variant="H2" color="text_3" className={'section-value'}>
            {totalDays} days
          </Text>
        </Box>
        <Box alignItems='center' display='flex' style={{ flex: 1 }}>
          <Box style={{ width: '100%', gap: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(17vw, 1fr))' }}>
            {disableDateList.map((item) => {
              const startTime = momentTimezone(item.startTime);
              const endTime = momentTimezone(item.endTime);
              return (
                <Box
                  key={item.id}
                  display="flex"
                  alignItems='center'
                  style={{ gap: 24 }}
                  className='time-block-item'
                >
                  <Box display='flex' alignItems='center'>
                    <Text
                      variant="H8"
                      color="text_3"
                      className="text-time-label"
                      style={{ width: '150px', textAlign: 'left' }}
                    >
                      {startTime.format('MM-DD-YYYY')}
                    </Text>
                    <Text
                      variant="H8"
                      color="text_3"
                      className="text-time-label"
                      style={{ width: '220px', textAlign: 'left', whiteSpace: 'nowrap' }}
                    >
                      {startTime.format('hh:mm A')} - {endTime.format('hh:mm A')}
                    </Text>
                  </Box>
                  <button type='button'
                    onClick={() => handleDelete(item.id)}
                    style={{ zoom: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <Icon type="circleClose" />
                  </button>
                </Box>
              );
            })}
          </Box>

        </Box>
      </TimeListContainer>
    </DaysOffListStyled>
  );
};

export default DaysOffList;

const TimeListContainer = styled(Box)`&.high-order {
background: #F6F7FC;
padding: 16px;
.time-block-item {
border-bottom: 1px solid #E9EDF2;
}
.section-label {
  color: #1D2129;
  font-family: Poppins;
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}
.section-value {
  color: #D2464F;
  font-family: Poppins;
  font-size: 32px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.text-time-label {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 20px; /* 111.111% */
  text-transform: capitalize;
}

}`;

const DaysOffListStyled = styled.div`
  margin-top: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #c9cdd4;
  .item-grow {
    flex-grow: 1;
  }

  &.v2 {
    .RangerContainerStyled .ant-picker .ant-picker-input input {
      color: #1d2129;
      font-family: Poppins;
      font-size: 18px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }

    .text-time-label {
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 500;
      line-height: normal;
    }
  }
`;
const RangerContainerStyled = styled(Form)`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  gap: 1rem;

  .ant-form-item .ant-form-item-label>label, .ant-form-item-label label, .ant-form-item-label label.basic_email {
    color: #505050;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;
const ItemFormStyled = styled(Form.Item)`
  margin: 0;
`;

const TimePickerStyled = styled(TimePicker)`
  .ant-picker-suffix {
    display: none;
  }
`;
const DatePickerStyled = styled(DatePicker)`
  .ant-picker-suffix {
    display: none;
  }
`;

const PickerContainer = styled(Box)`
  gap: 24px;
  .ant-picker {
    border-bottom: 1px solid black !important;
  }

  .ant-picker .ant-picker-input input {
    font-size: 24px !important;
    text-align: center;
  }
`;